<template>
  <div
    class="relative w-full bg-n-900 h-5 rounded-full px-1"
    @mousedown="startDrag"
    @touchstart.passive="startDrag"
    @mousemove="onDrag"
    @touchmove.passive="onDrag"
  >
    <div class="flex justify-between items-center h-full">
      <div
        v-for="(_point, idx) in points"
        :key="idx"
        class="w-4 h-4 border-2 border-transparent cursor-pointer flex justify-center items-center"
        :class="{
          invisible: idx === 0 || idx === points.length - 1
        }"
        @click="setCurrentIndex(idx)"
      >
        <div class="w-1 h-1 bg-n-700 rounded-full"></div>
      </div>
    </div>
    <div class="absolute inset-0 mx-3">
      <div
        class="absolute top-1/2 w-4 h-4 bg-g-300 border-2 border-n-50 rounded-full transform -translate-x-1/2 -translate-y-1/2 transition-all duration-300"
        :style="{
          left: `${currentIndex * (100 / (points.length - 1))}%`
        }"
      ></div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useEventListener } from '@vueuse/core'
import { computed, ref } from 'vue'

const props = defineProps<{ points: number[] }>()
const model = defineModel<number>()

const currentIndex = computed(() => {
  return props.points.findIndex(point => point === model.value)
})

const isDragging = ref(false)

function setCurrentIndex(index: number) {
  model.value = props.points[index]
}

function startDrag(event: MouseEvent | TouchEvent) {
  isDragging.value = true
  onDrag(event)
}

function stopDrag() {
  if (isDragging.value) {
    isDragging.value = false
  }
}

function onDrag(event: MouseEvent | TouchEvent) {
  if (!isDragging.value) return

  const slider = event.currentTarget as HTMLElement
  const rect = slider.getBoundingClientRect()
  const clientX = 'touches' in event ? event.touches[0].clientX : event.clientX
  const relativeX = clientX - rect.left
  const percentage = Math.max(0, Math.min(1, relativeX / rect.width))
  const newIndex = Math.round(percentage * (props.points.length - 1))

  setCurrentIndex(newIndex)
}

useEventListener(document, 'mouseup', stopDrag)
useEventListener(document, 'touchend', stopDrag)
</script>
